import { useState, useEffect, useCallback, useRef } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import {
  batchGetMetrics,
  BatchMetricsTarget,
} from '../api/marketplaceApi';
import { MarketplaceMetrics, MarketplaceTargetType } from 'types/marketplace';

export interface UseMarketplaceMetricsResult {
  metricsMap: Record<string, MarketplaceMetrics>;
  loading: boolean;
  error: string | null;
  fetchMetrics: (targets: BatchMetricsTarget[]) => void;
}

function buildKey(targetType: MarketplaceTargetType, targetId: string): string {
  return `${targetType}:${targetId}`;
}

export function useMarketplaceMetrics(): UseMarketplaceMetricsResult {
  const apiClients = useApiClientsOrNull();

  const [metricsMap, setMetricsMap] = useState<Record<string, MarketplaceMetrics>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pendingTargetsRef = useRef<BatchMetricsTarget[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const apiClientsRef = useRef(apiClients);
  apiClientsRef.current = apiClients;
  const metricsMapRef = useRef(metricsMap);
  metricsMapRef.current = metricsMap;
  const cancelledRef = useRef(false);

  const flushPending = useCallback(async () => {
    const client = apiClientsRef.current?.schemaClient;
    if (!client || pendingTargetsRef.current.length === 0) {
      return;
    }

    const targets = [...pendingTargetsRef.current];
    pendingTargetsRef.current = [];

    // API supports max 50 targets per batch
    const batches: BatchMetricsTarget[][] = [];
    for (let i = 0; i < targets.length; i += 50) {
      batches.push(targets.slice(i, i + 50));
    }

    setLoading(true);
    setError(null);

    try {
      const allMetrics: MarketplaceMetrics[] = [];
      for (const batch of batches) {
        if (cancelledRef.current) return;
        const response = await batchGetMetrics(client, batch);
        allMetrics.push(...response.metrics);
      }

      if (!cancelledRef.current) {
        setMetricsMap((prev) => {
          const next = { ...prev };
          for (const m of allMetrics) {
            next[buildKey(m.target_type, m.target_id)] = m;
          }
          return next;
        });
      }
    } catch (err) {
      if (!cancelledRef.current) {
        const message = err instanceof Error ? err.message : 'Failed to fetch metrics';
        setError(message);
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const fetchMetrics = useCallback((targets: BatchMetricsTarget[]) => {
    // Deduplicate against already-fetched and already-pending targets
    const newTargets = targets.filter((t) => {
      const key = buildKey(t.targetType, t.targetId);
      const alreadyFetched = key in metricsMapRef.current;
      const alreadyPending = pendingTargetsRef.current.some(
        (p) => p.targetType === t.targetType && p.targetId === t.targetId
      );
      return !alreadyFetched && !alreadyPending;
    });

    if (newTargets.length === 0) {
      return;
    }

    pendingTargetsRef.current = [...pendingTargetsRef.current, ...newTargets];

    // Batch requests with a short debounce to group rapid calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      flushPending();
    }, 50);
  }, [flushPending]);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return { metricsMap, loading, error, fetchMetrics };
}
